'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChickenTickResponseKindEnum } from '../services/generated/Api';
import { useChickenTick, useClaimChicken } from '../queries/useChickenQueries';
import { RESPAWN_COOLDOWN_MS, shouldShowSpawn } from './chickenSpawnLogic';

interface ChickenSpawn {
  spawnId: string | null;
  kind: 'regular' | 'cyber';
  amount: number;
  onClaim: () => void;
  onDespawn: () => void;
}

interface ShownSpawn {
  spawnId: string;
  kind: 'regular' | 'cyber';
  amount: number;
}

export function useChickenSpawn(): ChickenSpawn {
  const { data, dataUpdatedAt } = useChickenTick();
  const claim = useClaimChicken();

  const tickSpawnId = data?.spawnId ?? null;

  const [shownSpawn, setShownSpawn] = useState<ShownSpawn | null>(null);
  const claimedRef = useRef<Set<string>>(new Set());
  const cooldownUntilRef = useRef(0);

  useEffect(() => {
    if (
      tickSpawnId !== null &&
      shouldShowSpawn({
        tickSpawnId,
        shownSpawnId: shownSpawn?.spawnId ?? null,
        claimed: claimedRef.current,
        cooldownUntil: cooldownUntilRef.current,
        now: Date.now(),
      })
    ) {
      setShownSpawn({
        spawnId: tickSpawnId,
        kind: data?.kind === ChickenTickResponseKindEnum.Cyber ? 'cyber' : 'regular',
        amount: data?.amount ?? 500,
      });
    }
  }, [tickSpawnId, shownSpawn?.spawnId, dataUpdatedAt, data]);

  const onClaim = useCallback(() => {
    if (shownSpawn) {
      claimedRef.current.add(shownSpawn.spawnId);
      claim.mutate(shownSpawn.spawnId);
    }
  }, [claim, shownSpawn]);

  const onDespawn = useCallback(() => {
    cooldownUntilRef.current = Date.now() + RESPAWN_COOLDOWN_MS;
    setShownSpawn(null);
  }, []);

  return {
    spawnId: shownSpawn?.spawnId ?? null,
    kind: shownSpawn?.kind ?? 'regular',
    amount: shownSpawn?.amount ?? 500,
    onClaim,
    onDespawn,
  };
}
